import {
  DataGrid,
  type GridRowId,
  type GridRowSelectionModel,
} from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  MenuItem,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material'
import {
  SubscriptionsOutlined as SubscriptionsIcon,
  Edit as EditIcon,
  AutoAwesomeOutlined as GenerateIcon,
  Search as SearchIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
} from '@mui/icons-material'
import AddIcon from 'components/add-icon'
import { alpha } from '@mui/material/styles'
import type { AppState } from 'state/index'
import { useNavigate } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getCropLegends, getGlobalDemLegend } from 'api/legend'
import { find, noop } from 'es-toolkit/compat'
import type { CustomLegend } from 'state/field'
import { unitConvert } from '@farmtrx/units'
import { closeModal, showModal } from 'actions/app'
import EditLegend from 'components/layer-legend/edit-legend'
import DeleteLegend from './delete-crop-legend'
import GenerateCropLegends from './generate-crop-legends'
import { isSubscriptionValid } from 'common/misc'
import colors from 'common/colors'
import { TEST_IDS } from 'test-ids'
import { resolveSelectedRows } from './selection'

type CropLegendRow = {
  id: number
  name: string
  legendData: object
  tenantId: string
  parameter: string
  harvestId: number
  cropId: number
  // Null means the legend applies to every year.
  cropYear: number | null
  createdAt: string
  updatedAt: string
  buPerTonne: number
}

const ALL_YEARS = 'all'

export default function CropLegend() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useState('')
  const [yearFilter, setYearFilter] = useState<number | typeof ALL_YEARS>(
    ALL_YEARS,
  )
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set<GridRowId>([]),
    })
  const cropLegends: CustomLegend[] = useSelector(
    (state: AppState) => state.field.cropLegends,
  )
  // AppState types this as a bare ReactElement, but MODAL_SHOW stores a
  // { component, props } wrapper — hence the cast.
  const activeModal = useSelector(
    (state: AppState) => state.app.modal,
  ) as unknown as { component?: { type?: unknown } } | undefined
  const [cropLegendRows, setCropLegendRows] = useState<CropLegendRow[]>([])
  const [globalDemLegend, setGlobalDemLegend] = useState<CustomLegend | null>(
    null,
  )
  const preferenceUnits = useSelector(
    (state: AppState) => state.account.singleton.prefs?.units,
  )
  const cropConstants = useSelector(
    (state: AppState) => state.crop['cropConstantsCollection'],
  )
  const subscriptions = useSelector(
    (state: AppState) => state.account.subscriptions,
  )
  const [validSubscription, setValidSubscription] = useState(false)

  function convertData(data, param, buPerTonne) {
    let to =
      preferenceUnits?.['yieldunit_in'] === 'bu_ac'
        ? 'bu_us_ac'
        : preferenceUnits?.['yieldunit_in']
    let from = 'g_msq'
    if (param === 'moisture') {
      return data.toFixed(2)
    } else if (param === 'header_height') {
      to = preferenceUnits?.length_in
      from = 'mm'
      return unitConvert.convertLength(data, to, from).toFixed(2)
    } else if (param === 'speed') {
      to = preferenceUnits?.speed_in
      from = 'kmh'
      return unitConvert.convertSpeed(data, to, from).toFixed(2)
    }
    return unitConvert
      .convertYieldToPref(data, preferenceUnits?.['yieldunit_in'], buPerTonne)
      .toFixed(2)
  }

  const columns = [
    {
      field: 'name',
      headerName: t('name'),
      renderCell: (params) => {
        return t(params.row.name)
      },
    },
    {
      field: 'parameter',
      headerName: t('layer_type'),
      renderCell: (params) => {
        return t(params.row.parameter)
      },
    },
    {
      field: 'cropYear',
      headerName: t('crop_year'),
      width: 120,
      // valueGetter so sorting and the null label agree: pre-existing legends
      // and DEM legends have no season.
      valueGetter: (_value, row) => row.cropYear ?? null,
      renderCell: (params) =>
        params.row.cropYear ? params.row.cropYear : t('all_years'),
    },
    {
      field: 'legend',
      headerName: t('legend'),
      minWidth: 500,
      sortable: false,
      renderCell: (params) => {
        const c = params.row.legendData.ranges.c
        const q = params.row.legendData.ranges.q
        const param = params.row.parameter
        const width = 475 / params.row.legendData.num_ranges
        return (
          <Box
            sx={{
              flexDirection: 'row',
              display: 'flex',
              marginTop: '0.5rem',
            }}
          >
            {c?.map((color, index) => (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  alignItems: 'center',
                }}
                key={index}
              >
                <svg width={width} height="15" key={color}>
                  <rect
                    width="100%"
                    height="15"
                    strokeWidth="1"
                    stroke="rgb(0, 0, 0)"
                    fill={color}
                    key={color}
                  />
                </svg>
                <Typography>
                  {convertData(q[index], param, params.row.buPerTonne)}
                </Typography>
              </Box>
            ))}
          </Box>
        )
      },
    },
    {
      field: 'unit',
      headerName: t('unit'),
      sortable: false,
      renderCell: (params) => {
        const param = params.row.parameter
        let unit = preferenceUnits?.['yieldunit_in']
        if (param === 'moisture') {
          unit = '%'
        } else if (param === 'header_height') {
          unit = preferenceUnits?.length_in
        } else if (param === 'speed') {
          unit = preferenceUnits?.speed_in
        }
        return <Typography>{t(unit)}</Typography>
      },
    },
  ]

  useEffect(() => {
    const currentDate = new Date().toISOString().split('T')[0]
    setValidSubscription(
      isSubscriptionValid(subscriptions, currentDate, currentDate),
    )
  }, [subscriptions])

  useEffect(() => {
    //@ts-ignore
    dispatch(getCropLegends()).then((cropTemplates) => {
      populateRows(cropTemplates ?? [])
    })
  }, [cropConstants])

  useEffect(() => {
    if (cropLegends && cropLegends.length > 0) {
      // Saving from EditLegend relies on this to dismiss itself. The generate
      // dialog manages its own lifecycle instead — it stays open so several
      // proposals can be applied in one sitting — so leave it alone.
      if (activeModal?.component?.type !== GenerateCropLegends) {
        dispatch(closeModal())
      }
      populateRows(cropLegends)
    } else if (cropLegends && cropLegends.length === 0) {
      setCropLegendRows([])
    }
  }, [cropLegends, cropConstants])

  // Fetch the global DEM legend
  useEffect(() => {
    //@ts-ignore
    dispatch(getGlobalDemLegend()).then((demLegend) => {
      setGlobalDemLegend(demLegend)
    })
  }, [dispatch])

  const populateRows = useCallback(
    (cropTemplates) => {
      const legendRows = cropTemplates.map((template) => {
        const crop =
          cropConstants.length > 0
            ? cropConstants.find((c) => c.id === template.cropId)
            : null

        return {
          id: template.id,
          name: crop?.name || t('unknown_item', { item: t('crop') }),
          legendData: template.legendData,
          tenantId: template.tenantId,
          parameter: template.parameter,
          harvestId: template.harvestId,
          cropId: template.cropId,
          cropYear: template.cropYear ?? null,
          createdAt: template.createdAt,
          updatedAt: template.updatedAt,
          buPerTonne: crop?.buPerTonne,
        }
      })
      setCropLegendRows(legendRows)
    },
    [cropConstants],
  )

  const filterByYear = (year, legend) =>
    year === ALL_YEARS || legend.cropYear === year

  const filterCropLegends = (searchValue, legends) => {
    if (searchValue) {
      const lowerSearchValue = searchValue.toLowerCase()

      return legends.name?.toLowerCase().indexOf(lowerSearchValue) > -1
    }

    return true
  }

  /*
   * The rows the grid is actually showing. Computed here rather than inline in
   * the grid so the selection can be resolved against them: a selection made
   * before a filter changed still holds ids the user can no longer see, and
   * those must not be deleted.
   */
  const visibleRows = useMemo(
    () =>
      (Array.isArray(cropLegendRows) ? cropLegendRows : [])
        .map((c) => ({
          id: c.id,
          tenantId: c.tenantId,
          name: c.name,
          legendData: c.legendData,
          parameter: c.parameter,
          cropId: c.cropId,
          cropYear: c.cropYear,
          buPerTonne: c.buPerTonne,
        }))
        .filter((c) => filterCropLegends(searchValue, c))
        .filter((c) => filterByYear(yearFilter, c)),
    [cropLegendRows, searchValue, yearFilter],
  )

  const selectedRows = useMemo(
    () => resolveSelectedRows(visibleRows, rowSelectionModel),
    [rowSelectionModel, visibleRows],
  )

  const onSearch = (e) => {
    const _searchValue = e.target.value.toLowerCase()
    setSearchValue(_searchValue)
  }
  const clearSelection = useCallback(
    () =>
      setRowSelectionModel({ type: 'include', ids: new Set<GridRowId>([]) }),
    [],
  )

  const deleteCropLegend = useCallback(() => {
    if (selectedRows.length === 0) return
    // Resolve against cropLegendRows: the grid's rows carry what the columns
    // render, and the dialog needs the crop and season to name each legend.
    const selectedLegends = selectedRows.map(
      (row) => find(cropLegendRows, ['id', row.id]) ?? row,
    )
    dispatch(
      showModal(
        <DeleteLegend
          selectedLegends={selectedLegends}
          onDeleted={clearSelection}
        />,
      ),
    )
  }, [clearSelection, cropLegendRows, dispatch, selectedRows])

  const editCropLegend = useCallback(() => {
    if (selectedRows.length === 1) {
      const selectedCropLegend = find(cropLegends, ['id', selectedRows[0].id])
      if (selectedCropLegend) {
        const title = t('edit_legend', {
          legendType: t(selectedCropLegend.parameter),
        })
        dispatch(
          showModal(
            <EditLegend
              createCropLegend={false}
              crop={undefined}
              harvestId={undefined}
              layer={undefined}
              customLegend={selectedCropLegend}
              layerDetails={undefined}
              maxWidth="300px"
            />,
            { title: title },
          ),
        )
      }
    }
  }, [cropLegends, dispatch, selectedRows, t])

  const addCropLegend = useCallback(() => {
    dispatch(
      showModal(
        <EditLegend
          createCropLegend={true}
          crop={undefined}
          harvestId={undefined}
          layer={undefined}
          customLegend={undefined}
          layerDetails={undefined}
          maxWidth="300px"
          createdCropLegends={cropLegends}
        />,
        { title: t('create_crop_legend') },
      ),
    )
  }, [cropLegends])
  const generateFromHarvestData = useCallback(() => {
    dispatch(
      showModal(
        <GenerateCropLegends
          onGenerated={() => {
            //@ts-ignore
            dispatch(getCropLegends())
          }}
        />,
        { title: t('generate_crop_legends') },
      ),
    )
  }, [dispatch, t])

  // DEM legends are tenant-global and year-agnostic, so this dialog takes no
  // crop year and refreshes the DEM section rather than the grid.
  const generateDemLegend = useCallback(() => {
    dispatch(
      showModal(
        <GenerateCropLegends
          demOnly
          onGenerated={() => {
            //@ts-ignore
            dispatch(getGlobalDemLegend()).then((legend) =>
              setGlobalDemLegend(legend),
            )
          }}
        />,
        { title: t('generate_dem_legend') },
      ),
    )
  }, [dispatch, t])

  // Only the seasons that actually have legends, so the filter never offers an
  // option that empties the grid.
  const legendYears = useMemo(
    () =>
      Array.from(
        new Set(
          cropLegendRows
            .map((row) => row.cropYear)
            .filter((year): year is number => year != null),
        ),
      ).sort((a, b) => b - a),
    [cropLegendRows],
  )

  function CustomNoRowsOverlay() {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        {t('no_crop_legends_found')}
      </div>
    )
  }
  const deleteLabel = t('delete_item', {
    item: selectedRows.length > 1 ? t('crop_legends') : t('crop_legend'),
  })

  const onClick = noop

  return (
    <Box
      style={{
        overflowX: 'auto',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
      sx={{
        px: 3,
        py: 4,
        width: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h1>{t('legends')}</h1>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <IconButton
            disabled={!validSubscription || selectedRows.length === 0}
            onClick={deleteCropLegend}
            title={deleteLabel}
            aria-label={deleteLabel}
            size="large"
            data-testid={TEST_IDS.cropLegendDeleteButton}
          >
            <DeleteOutlineOutlinedIcon
              style={{
                color: selectedRows.length > 0 ? colors.brand : 'gray',
              }}
            />
          </IconButton>
          <IconButton
            title={t('edit_crop_legend')}
            aria-label={t('edit_crop_legend')}
            disabled={!validSubscription || selectedRows.length !== 1}
            onClick={editCropLegend}
            size="large"
          >
            <EditIcon
              style={{
                color: selectedRows.length === 1 ? colors.brand : 'gray',
              }}
            />
          </IconButton>

          <IconButton
            onClick={addCropLegend}
            title={t('add_crop_legend')}
            size="large"
            disabled={!validSubscription}
          >
            <AddIcon />
          </IconButton>
          <IconButton
            onClick={generateFromHarvestData}
            title={t('generate_crop_legends')}
            aria-label={t('generate_crop_legends')}
            size="large"
            disabled={!validSubscription}
            data-testid={TEST_IDS.generateCropLegendsButton}
          >
            <GenerateIcon style={{ color: colors.brand }} />
          </IconButton>
          <TextField
            select
            size="small"
            hiddenLabel
            value={yearFilter}
            onChange={(e) =>
              setYearFilter(
                e.target.value === ALL_YEARS
                  ? ALL_YEARS
                  : Number(e.target.value),
              )
            }
            sx={{ marginLeft: '10px', minWidth: '130px' }}
            slotProps={{ htmlInput: { 'aria-label': t('crop_year') } }}
            data-testid={TEST_IDS.cropLegendYearFilter}
          >
            <MenuItem value={ALL_YEARS}>{t('all_years')}</MenuItem>
            {legendYears.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            id="search"
            onChange={(val) => {
              onSearch(val)
            }}
            type="search"
            placeholder="Search"
            hiddenLabel
            sx={{ marginLeft: '10px' }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      </Box>
      <div
        style={{
          width: '100%',
          marginTop: '2rem',
        }}
      >
        {!validSubscription ? (
          <Box
            sx={{
              borderLeft: `8px solid ${alpha(colors.dangerAccent, 0.42)}`,
              background: alpha(colors.dangerAccent, 0.1),
              color: 'red',
              fontWeight: 'bold',
              paddingLeft: '4px',
            }}
            title={t('no_subscriptions_crop_legends')}
            onClick={() => navigate('/admin/subscription')}
          >
            <span> {t('renew_subscription')}</span>
            <SubscriptionsIcon
              style={{
                color: colors.dangerAccent,
                fontSize: '0.7em',
              }}
              titleAccess={t('no_subscriptions_crop_legends')}
            />
          </Box>
        ) : null}
        <DataGrid
          autoHeight
          checkboxSelection
          onRowSelectionModelChange={(selection: GridRowSelectionModel) => {
            setRowSelectionModel(selection)
          }}
          onRowClick={(params) => onClick(params.row)}
          initialState={{
            sorting: {
              // Newest season first: after a few years the list is long enough
              // that the current one has to be on top.
              sortModel: [
                { field: 'cropYear', sort: 'desc' },
                { field: 'name', sort: 'asc' },
              ],
            },
          }}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          rowSelectionModel={rowSelectionModel}
          //@ts-ignore
          rows={visibleRows}
          columns={columns}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: colors.greyBackground,
              textTransform: 'uppercase',
            },
          }}
          pageSizeOptions={[25, 50, 100]}
        />
      </div>
      {/* DEM Legend Section */}
      <Box
        sx={{
          marginTop: '3rem',
          paddingTop: '2rem',
          borderTop: `1px solid ${colors.greyBorder}`,
        }}
      >
        <h2>{t('dem_legend')}</h2>
        <Button
          variant="outlined"
          size="small"
          startIcon={<GenerateIcon />}
          disabled={!validSubscription}
          onClick={generateDemLegend}
          data-testid={TEST_IDS.generateDemLegendButton}
        >
          {t('generate_dem_legend')}
        </Button>
        {!globalDemLegend ? (
          <Typography sx={{ color: 'gray', marginTop: '1rem' }}>
            {t('no_dem_legend')}
          </Typography>
        ) : (
          <Box sx={{ marginTop: '1rem' }}>
            <Box sx={{ marginBottom: '1rem' }}>
              <Typography variant="body2">
                <strong>{t('dem_style_type')}:</strong>{' '}
                {t(globalDemLegend.legendData?.styleType || 'gradient')}
              </Typography>
              {globalDemLegend.legendData?.styleType !== 'hillshade' && (
                <Typography variant="body2">
                  <strong>{t('palette')}:</strong>{' '}
                  {t(globalDemLegend.legendData?.palette || 'terrain')}
                </Typography>
              )}
            </Box>

            {/* DEM Legend Visual Preview — not shown for hillshade (greyscale, no range data) */}
            {globalDemLegend.legendData?.styleType !== 'hillshade' && (
              <Box
                sx={{
                  flexDirection: 'row',
                  display: 'flex',
                  marginTop: '0.5rem',
                  marginBottom: '1rem',
                }}
              >
                {globalDemLegend.legendData?.ranges?.c?.map((color, index) => {
                  const quantity =
                    globalDemLegend.legendData?.ranges?.q?.[index]
                  // Convert from meters to user preference for display
                  const displayValue =
                    quantity !== undefined && preferenceUnits?.['length_in']
                      ? parseFloat(
                          unitConvert
                            .convertLength(
                              quantity,
                              preferenceUnits['length_in'],
                              unitConvert.METERS,
                            )
                            .toFixed(2),
                        )
                      : quantity
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        alignItems: 'center',
                      }}
                    >
                      <svg width={475 / 6} height="15">
                        <rect
                          width="100%"
                          height="15"
                          strokeWidth="1"
                          stroke="rgb(0, 0, 0)"
                          fill={color}
                        />
                      </svg>
                      <Typography variant="caption">{displayValue}</Typography>
                    </Box>
                  )
                })}
              </Box>
            )}

            {/* DEM Delete Button */}
            <Box>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteOutlineOutlinedIcon />}
                onClick={() => {
                  const handleDemLegendDeleted = () => {
                    setGlobalDemLegend(null)
                  }
                  dispatch(
                    showModal(
                      <DeleteLegend
                        selectedLegend={globalDemLegend}
                        onDemLegendDeleted={handleDemLegendDeleted}
                      />,
                    ),
                  )
                }}
              >
                {t('delete_item', { item: t('dem_legend') })}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
